import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { api, type Overview } from '@/api/client';
import { NeverSent, useNeverSent } from '@/components/NeverSent';
import { Trend } from '@/components/Sparkline';
import { TimeChart } from '@/components/TimeChart';
import { ago, compactCount, envStyle, formatCount } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { inTab, ISSUE_RANGES } from '@/lib/issues';
import { Link } from '@/lib/router';
import { useSettings } from '@/lib/settings';
import { usePageCommands, useShell } from '@/lib/shell';

// A colour per service, the same in every chart.
const SERVICE_COLOUR: Readonly<Record<string, string>> = {
    api: '#3987e5',
    cron: '#9085e9',
    'ms-media': '#2bc48c',
    frontend: '#fab219',
};
const colourOf = (service: string): string => SERVICE_COLOUR[service] ?? '#6a7c9f';

function readRange(): string {
    const range = new URLSearchParams(location.search).get('range') ?? '24h';

    return ISSUE_RANGES.some(([key]) => key === range) ? range : '24h';
}

// The buckets of a range, aligned on the step, and a series' values in them.
function bucketsOf(overview: Overview, from: number, to: number): number[] {
    const step = overview.stepMs;
    const start = Math.floor(from / step) * step;

    return Array.from({ length: Math.max(1, Math.ceil((to - start) / step)) }, (_, index) => start + index * step);
}

function seriesOf(overview: Overview, times: readonly number[], pick: (row: Overview['volume'][number]) => number, service?: string): number[] {
    const at = new Map(times.map((time, index) => [time, index]));
    const values = new Array<number>(times.length).fill(0);
    for (const row of overview.volume) {
        const index = at.get(row.t);
        if (index !== undefined && (service === undefined || row.service === service)) {
            values[index] = (values[index] ?? 0) + pick(row);
        }
    }

    return values;
}

// BOARDS: how an environment is doing, on one screen. Every number opens the lines behind it.
export function Boards(): ReactNode {
    const { env } = useShell();
    const neverSent = useNeverSent(env);
    const { timeZone: zone } = useSettings();
    const [range, setRange] = useState(readRange);
    const [anchor, setAnchor] = useState(() => Date.now());
    const now = useNow(15_000);
    const span = ISSUE_RANGES.find(([key]) => key === range)?.[2] ?? 24 * 3600_000;
    const scope = useMemo(() => ({ env, q: '', from: anchor - span, to: anchor }), [env, anchor, span]);

    useEffect(() => {
        const timer = setInterval(() => setAnchor(Date.now()), 60_000);

        return () => clearInterval(timer);
    }, []);
    useEffect(() => {
        history.replaceState(null, '', `/boards?env=${env}&range=${range}`);
    }, [env, range]);

    const overview = useQuery({
        queryKey: ['overview', scope],
        queryFn: ({ signal }) => api.overview(scope, signal),
        enabled: env !== '',
        placeholderData: (previous) => previous,
    });
    const issues = useQuery({ queryKey: ['issues', scope], queryFn: ({ signal }) => api.issues(scope, signal), enabled: env !== '' });
    const status = useQuery({ queryKey: ['status'], queryFn: api.status, refetchInterval: 10_000 });
    const data = overview.data;
    const times = useMemo(() => (data ? bucketsOf(data, scope.from, scope.to) : []), [data, scope.from, scope.to]);
    const services = useMemo(() => [...new Set((data?.volume ?? []).map((row) => row.service))].sort(), [data]);
    const needsEyes = (issues.data?.issues ?? []).filter((issue) => inTab(issue.status, 'open')).length;
    const regressed = (issues.data?.issues ?? []).filter((issue) => issue.status === 'regressed').length;
    const explore = (q: string): string => `/?env=${env}&q=${encodeURIComponent(q)}&range=${range === '1h' ? '1h' : range}`;
    const label = ISSUE_RANGES.find(([key]) => key === range)?.[1] ?? '24h';
    // Each number's shape over the range, for the strip at the top.
    const trend = useMemo(
        () => ({
            lines: data ? seriesOf(data, times, (row) => row.lines) : [],
            errors: data ? seriesOf(data, times, (row) => row.errors) : [],
            warnings: data ? seriesOf(data, times, (row) => row.warnings) : [],
            requests: data ? seriesOf(data, times, (row) => row.requests) : [],
        }),
        [data, times],
    );

    usePageCommands(ISSUE_RANGES.map(([key, rangeLabel]) => ({ icon: '◷', label: `Boards · the last ${rangeLabel}`, run: () => setRange(key) })));

    return (
        <div className="page page-scroll boards">
            <div className="page-head">
                <div>
                    <h1>
                        {envStyle(env).label} <span className="dim">· the last {label}</span>
                    </h1>
                    <p>Its volume, its errors, its traffic, its cron and its ingestion. Every number opens its lines.</p>
                </div>
                <div className="seg">
                    {ISSUE_RANGES.map(([key, rangeLabel]) => (
                        <button key={key} className={key === range ? 'on' : ''} onClick={() => setRange(key)}>
                            {rangeLabel}
                        </button>
                    ))}
                </div>
            </div>
            {neverSent && <NeverSent env={env} names={neverSent} panel />}
            {data && !neverSent && (
                <>
                    <div className="kpis">
                        <Link className="kpi hero" to={explore('')}>
                            <span className="l">Lines</span>
                            <b>{compactCount(data.totals.lines)}</b>
                            <span className="d">{formatCount(Math.round(data.totals.lines / Math.max(1, span / 60_000)))} a minute</span>
                            <Trend values={trend.lines} color="#6ca4f7" width={232} height={34} />
                        </Link>
                        <Link className="kpi bad" to={explore('level:>=error')}>
                            <span className="l">
                                <i style={{ background: 'var(--critical-ink)' }} />
                                Errors
                            </span>
                            <b>{compactCount(data.totals.errors)}</b>
                            <span className="d">
                                {((data.totals.errors / Math.max(1, data.totals.lines - data.totals.requests)) * 100).toFixed(2)}% of the lines
                            </span>
                            <Trend values={trend.errors} color="#e5484d" />
                        </Link>
                        <Link className="kpi" to={explore('level:warn')}>
                            <span className="l">
                                <i style={{ background: 'var(--warning)' }} />
                                Warnings
                            </span>
                            <b>{compactCount(data.totals.warnings)}</b>
                            <span className="d">{formatCount(Math.round(data.totals.warnings / Math.max(1, span / 3600_000)))} an hour</span>
                            <Trend values={trend.warnings} color="#fab219" />
                        </Link>
                        <Link className="kpi" to={explore('kind:request')}>
                            <span className="l">Requests</span>
                            <b>{compactCount(data.totals.requests)}</b>
                            <span className="d">{formatCount(Math.round(data.totals.requests / Math.max(1, span / 60_000)))} a minute</span>
                            <Trend values={trend.requests} color="#6ca4f7" />
                        </Link>
                        <Link className="kpi" to={explore('has:user')}>
                            <span className="l">Signed-in users</span>
                            <b>{compactCount(data.totals.users)}</b>
                            <span className="d">seen in the lines</span>
                        </Link>
                        <Link className={`kpi ${needsEyes > 0 ? 'bad' : ''}`} to={`/issues?env=${env}&range=${range}`}>
                            <span className="l">
                                {needsEyes > 0 && <i style={{ background: 'var(--critical-ink)' }} />}
                                Issues needing eyes
                            </span>
                            <b>{needsEyes}</b>
                            <span className="d">{regressed > 0 ? `${regressed} regressed` : 'none regressed'}</span>
                        </Link>
                    </div>
                    <div className="board-row two">
                        <div className="panel">
                            <div className="panel-head">
                                <span className="title">Lines by service</span>
                                <span className="note">request dumps apart · deploys marked</span>
                            </div>
                            <TimeChart
                                times={times}
                                stepMs={data.stepMs}
                                from={scope.from}
                                to={scope.to}
                                zone={zone}
                                series={services.map((service) => ({
                                    key: service,
                                    label: service,
                                    color: colourOf(service),
                                    values: seriesOf(data, times, (row) => row.lines, service),
                                }))}
                                markers={data.deploys.map((deploy) => ({ at: deploy.at, label: `${deploy.service} → ${deploy.commit.slice(0, 7)}` }))}
                                height={210}
                            />
                        </div>
                        <div className="panel">
                            <div className="panel-head">
                                <span className="title">Errors and warnings</span>
                                <span className="note">every service</span>
                            </div>
                            <TimeChart
                                times={times}
                                stepMs={data.stepMs}
                                from={scope.from}
                                to={scope.to}
                                zone={zone}
                                series={[
                                    { key: 'errors', label: 'errors', color: '#e5484d', values: seriesOf(data, times, (row) => row.errors) },
                                    { key: 'warnings', label: 'warnings', color: '#fab219', values: seriesOf(data, times, (row) => row.warnings) },
                                ]}
                                height={210}
                            />
                        </div>
                    </div>
                    <div className="board-row three">
                        <div className="panel">
                            <div className="panel-head">
                                <span className="title">Top errors</span>
                                <span className="note">by events</span>
                                <span className="spacer" />
                                <Link className="act" to={`/issues?env=${env}&range=${range}`}>
                                    All issues →
                                </Link>
                            </div>
                            <div className="toperrors">
                                {data.topErrors.map((error) => (
                                    <Link key={error.fingerprint} className="toperror" to={`/issues/${error.fingerprint}?env=${env}&range=${range}`}>
                                        <span className="te-title">{error.title}</span>
                                        <span className="te-msg">{error.message}</span>
                                        <span className="te-num mono">
                                            {compactCount(error.count)} · {compactCount(error.users)} users · {ago(error.lastSeen, now)} ago
                                        </span>
                                    </Link>
                                ))}
                                {data.topErrors.length === 0 && <div className="bars-empty micro">No error in the last {label}</div>}
                            </div>
                        </div>
                        <div className="panel">
                            <div className="panel-head">
                                <span className="title">Traffic</span>
                                <span className="note">top routes</span>
                            </div>
                            <RankedBars
                                rows={data.routes.map((route) => ({
                                    key: `${route.method} ${route.path}`,
                                    label: `${route.method} ${route.path}`,
                                    count: route.count,
                                }))}
                                tone="data"
                                empty="No request dump"
                            />
                        </div>
                        <div className="panel">
                            <div className="panel-head">
                                <span className="title">Error responses</span>
                                <span className="note">by status</span>
                            </div>
                            <RankedBars
                                rows={data.statuses.map((row) => ({
                                    key: String(row.status),
                                    label: String(row.status),
                                    count: row.count,
                                    to: explore(`status:${row.status}`),
                                }))}
                                tone="critical"
                                empty="No 4xx or 5xx"
                            />
                        </div>
                    </div>
                    <div className="board-row two">
                        <div className="panel">
                            <div className="panel-head">
                                <span className="title">The cron</span>
                                <span className="note">each job</span>
                            </div>
                            <table className="ptable compact">
                                <thead>
                                    <tr className="micro">
                                        <th>Job</th>
                                        <th className="num">Runs</th>
                                        <th className="num">Avg</th>
                                        <th className="num">Max</th>
                                        <th className="num">Errors</th>
                                        <th>Last run</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.cron.map((job) => {
                                        const late = now - job.lastRun > 10 * 60_000;

                                        return (
                                            <tr key={job.job} className="clickable">
                                                <td>
                                                    <Link className="mono" to={explore(`service:cron logger:${job.job}`)}>
                                                        {job.job}
                                                    </Link>
                                                </td>
                                                <td className="num mono">{formatCount(job.runs)}</td>
                                                <td className="num mono">{job.avgMs ? `${job.avgMs} ms` : '–'}</td>
                                                <td className="num mono">{job.maxMs ? `${job.maxMs} ms` : '–'}</td>
                                                <td className={`num mono ${job.errors > 0 ? 'warn' : ''}`}>{job.errors}</td>
                                                <td>
                                                    <span className={`dot ${late ? 'quiet' : 'ok'}`} />
                                                    {ago(job.lastRun, now)} ago
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {data.cron.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="ptable-empty">
                                                No cron line in the last {label}.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="board-side">
                            <div className="panel">
                                <div className="panel-head">
                                    <span className="title">Loudest modules</span>
                                    <span className="note">request dumps apart</span>
                                </div>
                                <RankedBars
                                    rows={data.modules.map((row) => ({
                                        key: row.module,
                                        label: row.module,
                                        count: row.lines,
                                        note:
                                            row.errors > 0
                                                ? `${compactCount(row.errors)} err`
                                                : row.warnings > 0
                                                  ? `${compactCount(row.warnings)} warn`
                                                  : undefined,
                                        to: explore(`module:"${row.module}"`),
                                    }))}
                                    tone="data"
                                    empty="Nothing"
                                />
                            </div>
                            <div className="panel">
                                <div className="panel-head">
                                    <span className="title">Ingestion</span>
                                    <span className="note">now</span>
                                    <span className="spacer" />
                                    <Link className="act" to="/sources">
                                        Sources →
                                    </Link>
                                </div>
                                <div className="versions">
                                    {(status.data?.sources ?? [])
                                        .filter((source) => source.env === env)
                                        .map((source) => (
                                            <div key={source.id} className="version">
                                                <span className="mono">
                                                    <span
                                                        className={`dot ${source.lastAt && now - Date.parse(source.lastAt) < 300_000 ? 'ok' : 'quiet'}`}
                                                    />
                                                    {source.name}
                                                </span>
                                                <span className="mono small">
                                                    {formatCount(Math.round(source.linesPerMinute))}/min ·{' '}
                                                    {source.lastAt ? `${ago(Date.parse(source.lastAt), now)} ago` : 'never'}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {!data && overview.isLoading && <div className="micro">Loading…</div>}
        </div>
    );
}

function RankedBars(props: {
    rows: Array<{ key: string; label: string; count: number; note?: string; to?: string }>;
    tone: 'data' | 'critical';
    empty: string;
}): ReactNode {
    const max = Math.max(1, ...props.rows.map((row) => row.count));
    if (props.rows.length === 0) {
        return <div className="bars-empty micro">{props.empty}</div>;
    }

    return (
        <div className={`bars ${props.tone}`}>
            {props.rows.map((row) => {
                const label = (
                    <span className="bar-label mono" title={row.label}>
                        {row.label}
                    </span>
                );

                return (
                    <div key={row.key} className="bar-row">
                        {row.to ? (
                            <Link to={row.to} className="bar-link">
                                {label}
                            </Link>
                        ) : (
                            label
                        )}
                        <span className="meter">
                            <b style={{ width: `${Math.max(3, (row.count / max) * 100)}%` }} />
                        </span>
                        <span className="mono small">
                            {row.note ? <span className="bar-note">{row.note} · </span> : null}
                            {compactCount(row.count)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
